/* eslint-disable no-param-reassign */
import React from "react";
import PropTypes from "prop-types";
import { pure } from "recompose";
import { List, Map } from "immutable";
import styled from "styled-components";
import GroupedInventoryList from "../GroupedInventoryList/GroupedInventoryList";
import constants from "../../utils/constants";
import Inventory from "../Inventory/Inventory";

const Container = styled.div`
  > :first-child {
    border-top: 1px solid #e1e1e1;
    border-bottom: 1px solid #e1e1e1;
  }
`;

/* TODO This Can be done on the Selectors SIDE!! */
function buildGroupingKey(inventory, criteria) {
  return (
    "key|" +
    criteria
      .map(bucket => {
        switch (bucket) {
          case constants.BUCKETS.CONDITION:
            return inventory.get("condition").get("ticker");
          case constants.BUCKETS.MEALPLAN:
            return inventory.get("mealplan").get("ticker");
          case constants.BUCKETS.CONFIGURATION:
            return inventory.get("configuration").get("ticker");
          case constants.BUCKETS.ACCOMMODATION:
            return "";
          default:
            return "";
        }
      })
      .join("|")
  );
}

/* TODO This Can be done on the Selectors SIDE!! */
function groupInventories(inventories, criteria) {
  return (
    inventories &&
    inventories.reduce((accumulator, inventory) => {
      const groupingKey = buildGroupingKey(inventory, criteria);
      if (!accumulator.get(groupingKey)) {
        accumulator = accumulator.set(groupingKey, new Map()); //eslint-disable-line
      }
      const selected = accumulator.get(groupingKey).get("selected");
      if (!selected) {
        accumulator = accumulator.setIn([groupingKey, "selected"], inventory.get("ticker"));
      }
      const list = accumulator.get(groupingKey).get("inventories") || new List();
      accumulator = accumulator.setIn([groupingKey, "inventories"], list.push(inventory));

      accumulator = accumulator.set(
        "totalAvailability",
        accumulator.get("totalAvailability") + inventory.get("availability")
      );

      return accumulator;
    }, new Map({ totalAvailability: 0 }))
  );
}

function InventoryList({
  inventoryGroupingCriteria,
  maxBookableRooms,
  numberOfRoomsLeftLimit,
  currency,
  startDate,
  endDate,
  numberOfNights,
  cancelRelease,
  calendarPriceVisibility,
  showBookingOptionSummary,
  showDiscountsCalculation,
  roomDetailRepresentationType,
  accommodationTicker,
  accommodationName,
  propertyServices,
  showCalendar,
  className,
  handleModalContent,
  handleCloseModal,
  handleScroll,
  submitBookingForm,
  onSelect,
  inventories,
  propertyTicker,
  cancellationRestrictions,
  getFirstNightCost,
  cancellationPoliciesInfo,
  inModification,
  isCartMaxItemsReached,
  loyalty,
  deviceType,
  userLoyaltyTier,
  depositRequiredTextConfiguration,
  depositNotRequiredTextConfiguration,
  linkToLoyaltyLogin,
  taxesText,
}) {
  let inventoriesTotalAvailability = 0;
  let inventoriesList = null;
  if (!inventoryGroupingCriteria || inventoryGroupingCriteria.size < 1) {
    inventoriesList =
      inventories.get(propertyTicker) &&
      inventories.get(propertyTicker).map((el, idx, list) => {
        inventoriesTotalAvailability += el.get("availability");
        return (
          <Inventory
            key={el.get("ticker")}
            ticker={el.get("ticker")}
            mealplan={el.get("mealplan")}
            condition={el.get("condition")}
            rackRate={el.get("rackRate")}
            discountPercentage={el.get("discountPercentage")}
            discountAmount={el.get("discountAmount")}
            averageRate={el.get("averageRate")}
            totalRate={el.get("totalRate")}
            loyaltyRate={el.get("loyaltyRate")}
            availability={el.get("availability")}
            maxBookableRooms={Math.min(maxBookableRooms, el.get("availability"))}
            showAvailabilityAlert={el.get("availability") <= numberOfRoomsLeftLimit}
            currency={currency}
            statusCode={el.get("statusCode")}
            statusFilterType={el.get("statusFilterType")}
            statusValue={el.get("statusValue")}
            configuration={el.get("configuration")}
            roomDenomination={el.get("roomDenomination")}
            onRoomSelect={numberOfUnits => onSelect({ inventory: el, numberOfUnits })}
            propertyTicker={propertyTicker}
            startDate={startDate}
            endDate={endDate}
            mode={constants.INVENTORY_MODE.LIST}
            defaultPrimaryText={el.get("configuration").get("name")}
            primaryText={el.get("configuration").get("name")}
            initiallyExpanded={list.size <= 1}
            numberOfNights={numberOfNights}
            submitBookingForm={submitBookingForm}
            handleModalContent={handleModalContent}
            cancelRelease={cancelRelease}
            handleScroll={handleScroll}
            priceCalendar={calendarPriceVisibility}
            handleCloseModal={handleCloseModal}
            showBookingOptionSummary={showBookingOptionSummary}
            showDiscountsCalculation={showDiscountsCalculation}
            roomDetailRepresentationType={roomDetailRepresentationType}
            accommodationTicker={accommodationTicker}
            accommodationName={accommodationName}
            fullWidth
            showCalendar={showCalendar}
            cancellationRestrictions={cancellationRestrictions}
            getFirstNightCost={getFirstNightCost}
            inventoryGroupingCriteria={inventoryGroupingCriteria}
            cancellationPoliciesInfo={cancellationPoliciesInfo}
            inModification={inModification}
            isCartMaxItemsReached={isCartMaxItemsReached}
            loyalty={loyalty}
            deviceType={deviceType}
            userLoyaltyTier={userLoyaltyTier}
            depositRequiredTextConfiguration={depositRequiredTextConfiguration}
            depositNotRequiredTextConfiguration={depositNotRequiredTextConfiguration}
            combined={el.getIn(["bookingOption", "combined"])}
            linkToLoyaltyLogin={linkToLoyaltyLogin}
            taxesText={taxesText}
          />
        );
      });
  } else {
    let groupedInventories = groupInventories(
      inventories.get(propertyTicker),
      inventoryGroupingCriteria
    );

    inventoriesTotalAvailability =
      groupedInventories && groupedInventories.get("totalAvailability");

    groupedInventories = groupedInventories && groupedInventories.delete("totalAvailability");

    inventoriesList = (
      <GroupedInventoryList
        cancelRelease={cancelRelease}
        criteria={inventoryGroupingCriteria}
        showBookingOptionSummary={showBookingOptionSummary}
        showDiscountsCalculation={showDiscountsCalculation}
        startDate={startDate}
        endDate={endDate}
        currency={currency}
        services={propertyServices}
        onRoomSelect={onSelect}
        propertyTicker={propertyTicker}
        maxBookableRooms={maxBookableRooms}
        numberOfRoomsLeftLimit={numberOfRoomsLeftLimit}
        numberOfNights={numberOfNights}
        submitBookingForm={submitBookingForm}
        handleModalContent={handleModalContent}
        handleScroll={handleScroll}
        handleCloseModal={handleCloseModal}
        roomDetailRepresentationType={roomDetailRepresentationType}
        groupedInventories={groupedInventories}
        accommodationTicker={accommodationTicker}
        accommodationName={accommodationName}
        fullWidth
        showCalendar={showCalendar}
        cancellationRestrictions={cancellationRestrictions}
        getFirstNightCost={getFirstNightCost}
        inventoryGroupingCriteria={inventoryGroupingCriteria}
        cancellationPoliciesInfo={cancellationPoliciesInfo}
        inModification={inModification}
        isCartMaxItemsReached={isCartMaxItemsReached}
        loyalty={loyalty}
        deviceType={deviceType}
        userLoyaltyTier={userLoyaltyTier}
        depositRequiredTextConfiguration={depositRequiredTextConfiguration}
        depositNotRequiredTextConfiguration={depositNotRequiredTextConfiguration}
        linkToLoyaltyLogin={linkToLoyaltyLogin}
        taxesText={taxesText}
      />
    );
  }

  return <Container className={className}>{inventoriesList}</Container>;
}

//TODO: Correct proptypes
// InventoryList.propTypes = {
//   className: PropTypes.string,
//   inventoryGroupingCriteria: PropTypes.string.isRequired,
//   maxBookableRooms: PropTypes.string.isRequired,
//   numberOfRoomsLeftLimit: PropTypes.string.isRequired,
//   currency: PropTypes.string.isRequired,
//   startDate: PropTypes.string.isRequired,
//   endDate: PropTypes.string.isRequired,
//   numberOfNights: PropTypes.string.isRequired,
//   cancelRelease: PropTypes.string.isRequired,
//   calendarPriceVisibility: PropTypes.string.isRequired,
//   showBookingOptionSummary: PropTypes.string.isRequired,
//   showDiscountsCalculation: PropTypes.string.isRequired,
//   roomDetailRepresentationType: PropTypes.string.isRequired,
//   accommodationName: PropTypes.string.isRequired,
//   propertyServices: PropTypes.string.isRequired,

//   handleModalContent: PropTypes.string.isRequired,
//   handleCloseModal: PropTypes.string.isRequired,
//   handleScroll: PropTypes.string.isRequired,
//   submitBookingForm: PropTypes.string.isRequired,
//   onSelect: PropTypes.string.isRequired,
//   inventories: PropTypes.string.isRequired,
//   propertyTicker: PropTypes.string.isRequired
// };

export default pure(InventoryList);
